import {
  Moon,
  Activity,
  Sparkles,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/Primitives";
import { CalendlyButton } from "@/components/Calendly";

const PRODUCTS_URL = "https://www.amway.ca/en_CA/myshop/restore";

const services = [
  {
    id: "1on1",
    num: "01",
    eyebrow: "1-on-1 Coaching",
    title: "Sleep Restriction Therapy",
    subtitle: "A monthly, deeply personalized 1:1 program for chronic insomnia.",
    price: "Starting at $200/mo",
    icon: Moon,
    cta: { type: "calendly", label: "Apply" },
    forWhom: [
      "Professionals with chronic insomnia or fragmented sleep",
      "Founders and operators running on caffeine + cortisol",
      "Anyone who has tried meditation apps, gadgets, and rituals — without lasting change",
    ],
    outcomes: [
      "Fall asleep within 15 minutes — consistently",
      "Sleep through the night with high efficiency",
      "Wake up clear-headed without alarm dependency",
      "A repeatable system that survives travel and work peaks",
    ],
    includes: [
      "Weekly 1:1 strategy sessions",
      "Custom sleep restriction protocol",
      "Daily check-ins via secure app",
      "Personalized sleep architecture review",
    ],
  },
  {
    id: "reset",
    num: "02",
    eyebrow: "Reset Program",
    title: "30-Day Sleep & Gut Reset",
    subtitle: "A structured 4-week protocol that rebuilds sleep and gut health together.",
    price: "Starting at $300",
    icon: Activity,
    cta: { type: "calendly", label: "Apply" },
    forWhom: [
      "High performers fighting burnout and brain fog",
      "Professionals with bloating, low energy, and poor recovery",
      "People who want a clear, finite, finish-line program",
    ],
    outcomes: [
      "Reset your circadian rhythm in 30 days",
      "Improve gut health, digestion, and mood stability",
      "Higher energy, sharper focus, deeper recovery",
      "A repeatable lifestyle blueprint",
    ],
    includes: [
      "30-day structured curriculum",
      "Sleep + nutrition + supplement plan",
      "Bi-weekly 1:1 calls",
      "Wearable data review (Oura / Whoop / Apple)",
    ],
  },
  {
    id: "tools",
    num: "03",
    eyebrow: "Products",
    title: "Recommended Sleep Products",
    subtitle: "Carefully selected tools and supplements to support better sleep, recovery, and daily energy.",
    price: "Starting at $30",
    icon: Sparkles,
    cta: { type: "external", label: "View Products", href: PRODUCTS_URL },
    forWhom: [
      "Self-driven professionals wanting expert curation",
      "Clients between programs looking to compound gains",
      "Anyone tired of guessing in the supplement aisle",
    ],
    outcomes: [
      "A vetted stack — no marketing noise",
      "Sleep, energy, and recovery tools tailored to your context",
      "Better ROI on every dollar spent on wellness",
      "Ongoing access to the latest validated picks",
    ],
    includes: [
      "Hand-picked supplement recommendations",
      "Devices for sleep, light, sound, and recovery",
      "Updates as the picks evolve",
      "Direct context on what to use, when, and why",
    ],
  },
];

const SERVICE_IMAGES = {
  "1on1":
    "https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/tume7y5z_sleep%20restriction.webp",
  reset:
    "https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/ag6g0d74_food.webp",
  tools:
    "https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/6wvtdxe6_justin%202.webp",
};

const Services = () => {
  return (
    <main data-testid="services-page" className="bg-white text-ink-900">
      {/* Hero */}
      <section className="relative pt-40 pb-20 md:pb-28 hero-halo">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-10 items-end">
            <Reveal className="col-span-12 lg:col-span-8">
              <SectionEyebrow testId="services-page-eyebrow">Programs</SectionEyebrow>
              <h1 className="mt-7 font-display font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[-0.025em] leading-[1.02] text-ink-900 break-words">
                Three programs.
                <br />
                <span className="text-brand-600">One outcome — your best sleep.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1} className="col-span-12 lg:col-span-4">
              <p className="text-ink-600 leading-[1.7]">
                Pick the entry point that matches your moment. Each program is
                built around your real schedule, biology, and the intensity
                you&apos;re ready for.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Service detail blocks */}
      <section className="relative py-16 md:py-20 bg-ink-50 border-y border-ink-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 space-y-8">
          {services.map((s, idx) => (
            <Reveal key={s.id} delay={idx * 0.05}>
              <article
                data-testid={`service-card-${s.id}`}
                className="surface p-8 md:p-12"
              >
                <div className="grid grid-cols-12 gap-10">
                  {/* Image column */}
                  <div className="col-span-12 lg:col-span-3 order-1">
                    <div className="rounded-2xl overflow-hidden border border-ink-100 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.10)] bg-ink-50 aspect-[4/5]">
                      <img
                        src={SERVICE_IMAGES[s.id]}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Title + meta */}
                  <div className="col-span-12 lg:col-span-4 order-2">
                    <div className="flex items-center gap-4">
                      <div className="h-11 w-11 rounded-xl bg-brand-50 flex items-center justify-center">
                        <s.icon size={20} className="text-brand-600" />
                      </div>
                      <span className="font-display text-[10px] uppercase tracking-[0.25em] text-ink-500 num-tag">
                        {s.num} — {s.eyebrow}
                      </span>
                    </div>
                    <h2 className="mt-7 font-display font-light text-2xl sm:text-3xl md:text-4xl tracking-[-0.02em] text-ink-900 leading-[1.05] break-words">
                      {s.title}
                    </h2>
                    <p className="mt-5 text-ink-600 leading-[1.7]">{s.subtitle}</p>
                    <p
                      data-testid={`service-price-${s.id}`}
                      className="mt-5 font-display text-sm uppercase tracking-[0.2em] text-brand-600"
                    >
                      {s.price}
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                    {s.cta.type === "external" ? (
                      <a
                        href={s.cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`service-cta-${s.id}`}
                        className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 rounded-full bg-brand-600 text-white text-[15px] font-medium hover:bg-brand-700 transition-all shadow-[0_2px_8px_rgba(37,99,235,0.20)] hover:shadow-[0_4px_14px_rgba(37,99,235,0.28)]"
                      >
                        {s.cta.label}
                        <ExternalLink
                          size={15}
                          className="transition-transform group-hover:translate-x-0.5"
                        />
                      </a>
                    ) : (
                      <CalendlyButton
                        testId={`service-cta-${s.id}`}
                        className="w-full sm:w-auto justify-center"
                      >
                        {s.cta.label}
                      </CalendlyButton>
                    )}
                    </div>
                  </div>

                  {/* Lists */}
                  <div className="col-span-12 lg:col-span-5 grid grid-cols-1 md:grid-cols-3 gap-8 order-3">
                    <div>
                      <h4 className="font-display text-[10px] uppercase tracking-[0.25em] text-ink-500 mb-5">
                        Who It&apos;s For
                      </h4>
                      <ul className="space-y-3 text-sm text-ink-700 leading-[1.65]">
                        {s.forWhom.map((w) => (
                          <li key={w} className="flex gap-2">
                            <span className="text-brand-600 mt-1">·</span>
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-display text-[10px] uppercase tracking-[0.25em] text-ink-500 mb-5">
                        Your Outcomes
                      </h4>
                      <ul className="space-y-3 text-sm text-ink-900 leading-[1.65]">
                        {s.outcomes.map((o) => (
                          <li key={o} className="flex gap-2">
                            <CheckCircle2
                              size={14}
                              className="text-brand-600 mt-0.5 flex-shrink-0"
                            />
                            {o}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-display text-[10px] uppercase tracking-[0.25em] text-ink-500 mb-5">
                        What&apos;s Included
                      </h4>
                      <ul className="space-y-3 text-sm text-ink-700 leading-[1.65]">
                        {s.includes.map((i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-ink-400 mt-1">—</span>
                            {i}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl sm:text-4xl md:text-5xl tracking-tight text-ink-900 leading-[1.1]">
              Not sure which program fits?
            </h2>
            <p className="mt-5 text-ink-600 max-w-xl mx-auto leading-relaxed">
              Book a free 20-minute sleep assessment. We&apos;ll diagnose the
              bottleneck and recommend the right path — even if it isn&apos;t
              with me.
            </p>
            <div className="mt-9 flex justify-center">
              <CalendlyButton testId="services-bottom-cta">
                Book a Free Assessment
              </CalendlyButton>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Services;
